import classNames from 'classnames'
import type { FunctionComponent } from 'react'

import styles from './desktop-comparison-table.module.scss'
import { getTableBodyRows, getTableHeaderRows } from './util'
import { RawAnchor } from '@components/anchor'
import { Check } from '@components/icons/check'
import { Close } from '@components/icons/close'
import { Image } from '@components/image'
import { Markdown } from '@components/markdown'
import { Desktop } from '@components/responsive'
import { Text } from '@components/text'
import { ActionButton } from '@contentful-components/action-button'
import type {
    ComparisonTableFragment,
    PlanBenefitFragment,
    PlanBenefitValueFragment,
    PlanFragment,
} from '@graphql/fragments/ComparisonTableFragment.graphql'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { renderTemplate, TEXT } from '@lib/utils/text'

const Spacer = ({ tag = 'td' }: { tag?: 'td' | 'th' }) => {
    const Component = tag
    return <Component className={styles.spacer} role='presentation' />
}

const HeaderPlanCell = ({ theme, title, description, image }: PlanFragment) => (
    <th className={classNames(styles['header-cell'], styles[`theme-${theme}`])}>
        {image?.source && (
            <Image
                source={image.source}
                className={styles['plan-image']}
                alt={image.alt}
            />
        )}
        {title && (
            <Text
                tag='h3'
                typeStyle='body-20'
                className={styles['plan-title']}
                bold
            >
                {title}
            </Text>
        )}
        {description && (
            <Text
                tag='div'
                typeStyle='body-xs'
                className={styles['plan-description']}
            >
                {description}
            </Text>
        )}
    </th>
)

const FooterPlanCell = ({ button }: PlanFragment) => (
    <td align='center'>
        {!!button && <ActionButton preset={button?.preset} {...button} />}
    </td>
)

const HeaderRow: FunctionComponent<{
    title?: string
    plans: ComparisonTableFragment['plans']
}> = ({ title, plans }) => {
    const headerRows = getTableHeaderRows(plans)

    return (
        <tr className={styles.header}>
            <th
                className={classNames(
                    styles['header-cell'],
                    styles['header-title'],
                )}
            >
                <Text tag='div' typeStyle='body-2xl'>
                    {title}
                </Text>
            </th>
            <Spacer tag='th' />
            {headerRows.map((plan, index) => {
                // null is a placeholder for a spacer
                if (plan === null)
                    return <Spacer tag='th' key={`header-row-${index}`} />

                return (
                    <HeaderPlanCell
                        key={`header-plan-${plan.sys?.id}`}
                        {...plan}
                    />
                )
            })}
        </tr>
    )
}

const BenefitCell: FunctionComponent<{
    benefit: PlanBenefitFragment | undefined
    image: PlanBenefitFragment['image'] | undefined
}> = ({ benefit, image }) => {
    const personalizations = usePersonalizations()
    if (!benefit?.title) return null

    return (
        <td className={classNames(styles.cell, styles['benefit-cell'])}>
            <div className={styles['benefit-cell-title-image-container']}>
                {image?.source && (
                    <Image
                        source={image.source}
                        className={styles['benefit-cell-icon']}
                        alt={image.alt}
                        width={60}
                        height={60}
                    />
                )}
                <Markdown
                    tag='div'
                    typeStyle='body-20'
                    markdown={renderTemplate(benefit?.title, personalizations)}
                    color='white'
                />
            </div>
            {benefit?.description && (
                <Markdown
                    typeStyle={{ _: 'body-lg', md: 'body-md' }}
                    markdown={renderTemplate(
                        benefit?.description,
                        personalizations,
                    )}
                    color='white'
                />
            )}
            {benefit?.link && (
                <RawAnchor
                    href={benefit?.link}
                    preset='plain'
                    className={styles.link}
                    onClick={(e) => e.stopPropagation()}
                >
                    {TEXT.learnMore}
                </RawAnchor>
            )}
        </td>
    )
}

const PlanBenefitCell: FunctionComponent<
    Omit<PlanBenefitValueFragment, 'type' | 'sys'> & {
        theme?: PlanFragment['theme']
        isLast?: boolean
        isEmpty?: boolean
    }
> = ({ value, theme, isLast, isEmpty }) => {
    const personalizations = usePersonalizations()

    if (isEmpty) return <td className={styles.cell} />

    // if the string value has length, return it, otherwise return a checkmark to show the benefit is included
    const content = value || <Check width='23' color='step-white' />

    return (
        <td
            className={classNames(
                styles.cell,
                styles['plan-value'],
                styles[`theme-${theme}`],
                isLast && styles['last-cell'],
            )}
        >
            <div className={styles['plan-value-outer']}>
                <div className={styles['plan-value-inner']}>
                    {typeof content === 'string' && content === '-' && (
                        <Close width='23' color='step-white' />
                    )}

                    {typeof content === 'string' &&
                        content.toLowerCase() === 'yes' && (
                            <Check width='23' color='step-white' />
                        )}

                    {/* typeof content === 'object' is used to support the existing checkmark objects  */}
                    {typeof content === 'object' && (
                        <Text
                            tag='div'
                            typeStyle='body-20'
                            bold
                            className={styles['plan-benefit-value']}
                        >
                            {content}
                            <Check width='23' color='step-white' />
                        </Text>
                    )}

                    {/* typeof content === 'string' is used to support markdown text with symbols  */}
                    {typeof content === 'string' &&
                        content !== '-' &&
                        content.toLowerCase() !== 'yes' && (
                            <Markdown
                                tag='div'
                                typeStyle='body-20'
                                markdown={renderTemplate(
                                    content.toString(),
                                    personalizations,
                                )}
                                color='white'
                            />
                        )}
                </div>
            </div>
        </td>
    )
}

const Body = ({
    benefits,
    plans,
}: {
    benefits: ComparisonTableFragment['benefits']
    plans: ComparisonTableFragment['plans']
}) => {
    const bodyRows = getTableBodyRows(benefits, plans)

    return (
        <>
            {bodyRows.map((row, i) => (
                <tr key={`body-row-${i}`} className={styles.row}>
                    {row.map((cell, j) => {
                        switch (cell?.type) {
                            case 'benefit':
                                return (
                                    <BenefitCell
                                        benefit={cell.benefit}
                                        key={cell.benefit?.key}
                                        image={cell.benefit?.image}
                                    />
                                )
                            case 'planBenefit':
                                return (
                                    <PlanBenefitCell
                                        key={`body-row-cell-plan-${i}-${j}`}
                                        value={cell.value?.value}
                                        isLast={cell.isLast}
                                        theme={cell.theme}
                                        isEmpty={cell.isEmpty}
                                    />
                                )
                            default:
                                return (
                                    <Spacer
                                        key={`body-row-cell-spacer-${i}-${j}`}
                                    />
                                )
                        }
                    })}
                </tr>
            ))}
        </>
    )
}

const TableFooter: FunctionComponent<{
    title?: string
    plans: ComparisonTableFragment['plans']
}> = ({ plans }) => {
    const footerRows = getTableHeaderRows(plans)

    return (
        <tr className={styles.header}>
            <th
                className={classNames(
                    styles['header-cell'],
                    styles['header-title'],
                )}
            />
            <Spacer tag='th' />
            {footerRows.map((plan, index) => {
                // null is a placeholder for a spacer
                if (plan === null)
                    return <Spacer tag='th' key={`header-row-${index}`} />

                return (
                    <FooterPlanCell
                        key={`footer-plan-${plan.sys?.id}`}
                        {...plan}
                    />
                )
            })}
        </tr>
    )
}

export const DesktopComaprisonTable: FunctionComponent<
    Omit<ComparisonTableFragment, 'type' | 'sys'>
> = ({ title, benefits, plans }) => (
    <Desktop>
        <table className={styles.table}>
            <thead className={styles.header}>
                <HeaderRow title={title} plans={plans} />
            </thead>
            <tbody>
                <Body benefits={benefits} plans={plans} />
            </tbody>
            <tfoot>
                <TableFooter plans={plans} />
            </tfoot>
        </table>
    </Desktop>
)
